import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { Loader2, ExternalLink, HelpCircle, History } from 'lucide-react';

const AutomationRuns = ({ automationId, onSelectRun }) => {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');

    const fetchRuns = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/inbound-automations/${automationId}/runs?page=${page}&status=${statusFilter}`);
            setRuns(res.data.runs);
            setTotal(res.data.total);
        } catch (err) {
            console.error('Failed to fetch runs:', err);
        } finally {
            setLoading(false);
        }
    }, [automationId, page, statusFilter]);

    useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'COMPLETED': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'FAILED': return 'bg-red-500/10 text-red-500 border-red-500/20';
            case 'PARTIAL_SUCCESS': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
            case 'SKIPPED': return 'bg-slate-500/10 text-slate-500 border-slate-500/20';
            case 'SUPPRESSED': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
            case 'RUNNING': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            default: return 'bg-slate-500/10 text-slate-500 border-slate-500/20';
        }
    };

    if (loading && page === 1) {
        return (
            <div className="flex flex-col items-center justify-center p-20 text-muted-foreground gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium">Loading execution history...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-card/30">
            <div className="p-4 border-b border-border/50 flex items-center justify-between bg-card/50 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-bold">Execution History</h3>
                    <Badge variant="outline" className="ml-2 bg-muted/50">{total} runs</Badge>
                </div>
                <div className="flex items-center gap-2">
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-background border border-border/50 rounded-md text-[10px] px-2 py-1 outline-none ring-primary/20 focus:ring-2"
                    >
                        <option value="">All Status</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="FAILED">Failed</option>
                        <option value="PARTIAL_SUCCESS">Partial Success</option>
                        <option value="SKIPPED">Skipped</option>
                        <option value="SUPPRESSED">Suppressed</option>
                    </select>
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                {runs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-20 text-muted-foreground/50 text-center">
                        <History className="h-12 w-12 mb-4 opacity-10" />
                        <p className="text-sm">No execution history found yet.</p>
                        <p className="text-[10px]">When this automation triggers, runs will appear here.</p>
                    </div>
                ) : (
                    <>
                        <Table>
                            <TableHeader className="bg-muted/30">
                                <TableRow>
                                    <TableHead className="w-[180px] text-[10px] uppercase font-bold tracking-wider">Time</TableHead>
                                    <TableHead className="text-[10px] uppercase font-bold tracking-wider">Status</TableHead>
                                    <TableHead className="text-[10px] uppercase font-bold tracking-wider">Signal</TableHead>
                                    <TableHead className="text-[10px] uppercase font-bold tracking-wider">Source</TableHead>
                                    <TableHead className="text-right text-[10px] uppercase font-bold tracking-wider">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {runs.map((run) => (
                                    <TableRow key={run._id} className="group hover:bg-primary/5 transition-colors cursor-pointer" onClick={() => onSelectRun(run._id)}>
                                        <TableCell className="text-[11px] font-medium font-mono text-muted-foreground">
                                            {format(new Date(run.createdAt), 'MMM d, HH:mm:ss')}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`text-[9px] px-1.5 py-0 border ${getStatusStyle(run.status)}`}>
                                                {run.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-[11px]">
                                            <div className="flex items-center gap-1.5 font-bold text-foreground/80 lowercase">
                                                <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
                                                {run.signalType || 'UNKNOWN'}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-[11px] text-muted-foreground font-medium uppercase tracking-tighter">
                                            {run.sourceType?.replace('_', ' ') || 'SYSTEM'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <ExternalLink className="h-3 w-3" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        {total > (page * 20) && (
                            <div className="p-3 border-t border-border/50 flex items-center justify-center gap-4 bg-muted/20">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    disabled={page === 1} 
                                    onClick={() => setPage(p => p - 1)}
                                    className="text-[10px] h-7"
                                >
                                    Previous
                                </Button>
                                <span className="text-[10px] font-bold text-muted-foreground">Page {page}</span>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    disabled={runs.length < 20} 
                                    onClick={() => setPage(p => p + 1)}
                                    className="text-[10px] h-7"
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Status Legend & Retention Info */}
            <div className="p-4 border-t border-border/50 bg-card/60 backdrop-blur-sm shrink-0">
                <div className="flex items-center justify-between gap-6">
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                        <div className="flex items-center gap-1.5" title="Run identified as a duplicate at the signal level.">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground">Suppressed: Duplicate Signal</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Signal processed, but no condition filters matched.">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                            <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground">Skipped: No Path Matched</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Some actions succeeded before an error occurred.">
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                            <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground">Partial: Error Mid-Flow</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10">
                        <HelpCircle className="h-3 w-3 text-primary/60" />
                        <span className="text-[9px] font-bold text-muted-foreground whitespace-nowrap">
                            Retention: Logs older than 30 days are purged.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationRuns;
